import { type BaseSyntheticEvent } from 'react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Stack, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { AppState } from 'state/index'
import { isEmpty } from 'es-toolkit/compat'

const swathParameters = ['yield', 'header_height', 'moisture', 'speed']
const swathParametersTranslationKey = [
  'yield',
  'header_height',
  'moisture',
  'speed',
]
const swathParametersSldMap = ['yield', 'headerheight', 'moisture', 'speed']
const defaultSwathParameterStatus = [false, false, false, false]

interface SwathParameterSelectorProps {
  swathParameter: string
  layerInfoMap: any
  onChange: Function
}

const SwathParameterSelector: React.FC<SwathParameterSelectorProps> = ({
  swathParameter,
  layerInfoMap,
  onChange,
}) => {
  const { t } = useTranslation()

  const wmsLayers = useSelector((state: AppState) => state.map.wmsLayers)

  const [swathParameterStatus, setSwathParameterStatus] = useState(
    defaultSwathParameterStatus,
  )

  const getDefaultSwathParameterStatus = (metadata) => {
    if (!metadata?.swath_columns || metadata?.swath_columns.length == 0)
      return [false, false, false, false]

    // Map swathParameters to boolean based on availability
    return swathParameters.map((param) => {
      switch (param) {
        case 'header_height':
          return metadata?.swath_columns.includes('header_height')
        case 'yield':
          return metadata?.swath_columns.includes('yield')
        case 'moisture':
          return metadata?.swath_columns.includes('moisture')
        case 'speed':
          return metadata?.swath_columns.includes('speed')
        default:
          return false
      }
    })
  }

  useEffect(() => {
    if (layerInfoMap['swath']?.metadata?.swath_columns) {
      if (
        !layerInfoMap['swath'].metadata?.swath_columns?.includes(swathParameter)
      ) {
        onChange({
          selectedIndex: 0,
          swathParameter: swathParameters[0],
          sldExtension: `_${swathParametersSldMap[0]}.sld`,
        })
      }
    }

    if (!isEmpty(layerInfoMap)) {
      setSwathParameterStatus(
        getDefaultSwathParameterStatus(layerInfoMap['swath'].metadata),
      )
    }
  }, [wmsLayers, layerInfoMap, swathParameter])

  const handleSwathParameterChange = (event: BaseSyntheticEvent) => {
    const selectedIndex = event.target.options.selectedIndex
    onChange({
      selectedIndex: selectedIndex,
      swathParameter: event.target.value,
      sldExtension: `_${swathParametersSldMap[selectedIndex]}.sld`,
    })
  }

  return swathParameterStatus.some((s) => s === true) ? (
    <Stack direction="row">
      <TextField
        select
        label={t('swath_parameter')}
        value={swathParameter}
        size="small"
        margin="dense"
        fullWidth
        slotProps={{
          select: {
            native: true,
          },
        }}
        sx={{
          margin: '7px',
          fontSize: '0.75em',
          height: '28px',
        }}
        onChange={handleSwathParameterChange}
      >
        {swathParameters.map((param, index) => (
          <option
            key={param}
            value={param}
            disabled={!swathParameterStatus[index]}
          >
            {t(swathParametersTranslationKey[index])}
          </option>
        ))}
      </TextField>
    </Stack>
  ) : (
    ''
  )
}

export default SwathParameterSelector
export { swathParameters }
